import { useLayoutEffect, useState } from "react";
import type { CSSProperties, RefObject } from "react";

type UseSnappedVideoContainerStyleParams = {
  asset_container_ref: RefObject<HTMLDivElement | null>;
  should_snap_height: boolean;
};

const MOBILE_VIDEO_PIXEL_SNAP_MEDIA_QUERY = "(max-width: 767.98px)";
const VIDEO_ASPECT_RATIO_HEIGHT = 9;
const VIDEO_ASPECT_RATIO_WIDTH = 16;

export function use_snapped_video_container_style({
  asset_container_ref,
  should_snap_height,
}: UseSnappedVideoContainerStyleParams): CSSProperties | undefined {
  const [snapped_height_px, set_snapped_height_px] = useState<number | null>(
    null,
  );

  useLayoutEffect(() => {
    if (
      !should_snap_height ||
      !("matchMedia" in window) ||
      !("ResizeObserver" in window)
    ) {
      set_snapped_height_px(null);
      return;
    }

    const asset_container = asset_container_ref.current;

    if (!asset_container) {
      set_snapped_height_px(null);
      return;
    }

    const mobile_media_query = window.matchMedia(
      MOBILE_VIDEO_PIXEL_SNAP_MEDIA_QUERY,
    );

    const update_snapped_height = (container_width_px: number) => {
      set_snapped_height_px(
        mobile_media_query.matches ?
          get_snapped_video_container_height_px(container_width_px)
        : null,
      );
    };
    const handle_media_query_change = () => {
      update_snapped_height(asset_container.getBoundingClientRect().width);
    };
    const resize_observer = new ResizeObserver(resize_observer_entries => {
      const container_width_px =
        resize_observer_entries[0]?.contentRect.width ??
        asset_container.getBoundingClientRect().width;

      update_snapped_height(container_width_px);
    });

    resize_observer.observe(asset_container);
    mobile_media_query.addEventListener("change", handle_media_query_change);
    handle_media_query_change();

    return () => {
      resize_observer.disconnect();
      mobile_media_query.removeEventListener(
        "change",
        handle_media_query_change,
      );
    };
  }, [asset_container_ref, should_snap_height]);

  if (snapped_height_px === null) {
    return undefined;
  }

  return {
    height: `${snapped_height_px}px`,
  };
}

export function get_snapped_video_container_height_px(
  container_width_px: number,
) {
  return Math.ceil(
    Math.max(container_width_px, 0) *
      (VIDEO_ASPECT_RATIO_HEIGHT / VIDEO_ASPECT_RATIO_WIDTH),
  );
}
